import {
    Icon,
    IconColor,
    IconID,
    IconSize,
} from '@thoughtspot/radiant-react/widgets/icon';
import { Horizontal } from '@thoughtspot/radiant-react/widgets/layout';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import Lottie from 'lottie-react';
import React, { useEffect, useState } from 'react';
import {
    CircularProgress,
    Size,
} from '../../../sparkflow/utils/circular-progress/circular-progress';
import { checklistDataTestId } from '../trial-info.util';
import styles from './checklist-modal.module.scss';

interface ChecklistAccordionHeaderProps {
    title: string;
    checkboxId: string;
    checkedItems: Set<string>;
    isSectionHeader?: boolean;
    sectionTaskIds?: string[];
    lastCompletedTaskId?: string | null;
}

export const ChecklistAccordionHeader: React.FC<ChecklistAccordionHeaderProps> = ({
    title,
    checkboxId,
    checkedItems,
    isSectionHeader = false,
    sectionTaskIds = [],
    lastCompletedTaskId,
}) => {
    const totalCount = sectionTaskIds?.length;
    const completedCount =
        isSectionHeader && sectionTaskIds?.length > 0
            ? sectionTaskIds?.filter(id => checkedItems.has(id))?.length
            : 0;
    const isSectionComplete = totalCount > 0 && completedCount === totalCount;

    const isJustCompleted = lastCompletedTaskId === checkboxId;
    const [showAnimation, setShowAnimation] = useState(isJustCompleted);
    const [animationData, setAnimationData] = useState<object | null>(null);

    useEffect(() => {
        if (isJustCompleted) {
            setShowAnimation(true);
        }
    }, [isJustCompleted]);

    useEffect(() => {
        if (!showAnimation || animationData) return undefined;

        const controller = new AbortController();
        fetch('/img/lottie/checklist-item-animation.json', {
            signal: controller.signal,
        })
            .then(res => res.json())
            .then(data => {
                if (!controller.signal.aborted) setAnimationData(data);
                return data;
            })
            .catch(() => {
                if (!controller.signal.aborted) setShowAnimation(false);
            });
        return () => {
            controller.abort();
        };
    }, [showAnimation, animationData]);

    const renderStatusIcon = () => {
        // icon radiant has either l as 18px
        // or xl as 24px
        const checkListIconSize = '20px';

        const iconStyle = {
            width: checkListIconSize,
            height: checkListIconSize,
        };

        if (!isSectionHeader) {
            if (showAnimation && animationData) {
                return (
                    <Lottie
                        animationData={animationData}
                        loop={false}
                        autoplay
                        className={styles.checklistAnimation}
                        onComplete={() => setShowAnimation(false)}
                        onDataFailed={() => setShowAnimation(false)}
                    />
                );
            }

            return (
                <Icon
                    id={IconID.CHECKMARK_CIRCLE}
                    color={
                        checkedItems.has(checkboxId)
                            ? IconColor.PURPLE
                            : IconColor.LIGHTMEDIUMGRAY
                    }
                    size={IconSize.LARGE}
                    className={styles.checklistIcon}
                    style={iconStyle}
                    data-testid={checklistDataTestId.CHECKLIST_ITEM_CHECKED}
                />
            );
        }

        if (isSectionComplete) {
            return (
                <Icon
                    id={IconID.CHECKMARK_CIRCLE}
                    color={IconColor.PURPLE}
                    size={IconSize.LARGE}
                    className={styles.checklistIconHeader}
                    style={iconStyle}
                />
            );
        }

        return (
            <CircularProgress
                steps={totalCount}
                currentStep={completedCount}
                size={Size.SMEDIUM}
                className={styles.circularProgressHeader}
            />
        );
    };

    return (
        <Horizontal
            className={styles.checklistItemHeader}
            data-testid={
                isSectionHeader
                    ? checklistDataTestId.CHECKLIST_SECTION_HEADER_BUTTON
                    : checklistDataTestId.CHECKLIST_TASK_HEADER_BUTTON
            }
        >
            <Typography
                variant="body-normal"
                ellipsis={{ rows: 1 }}
                className={styles.checklistItemTitle}
            >
                {title}
            </Typography>
            {renderStatusIcon()}
        </Horizontal>
    );
};
